import React from 'react';
import { Badge } from '../ui/badge';
import { CircleCheck, CircleAlert, Pencil, ExternalLink } from 'lucide-react';
import { fmtCurrency, fmtDate } from './helpers';

const BillRow = ({ bill, agentId, onEditBill, onPayment, isSelected, onToggleSelect, isAdmin, hasPct2, onViewBill }) => {
  const hasDouble = bill.commission_pct_2 > 0;
  const commTooltip = bill.is_manual
    ? `Manual: ${fmtCurrency(bill.commission_amount)}`
    : hasDouble && bill.commission_amt_2 > 0
      ? `${fmtCurrency(bill.commission_amt_1)} + ${fmtCurrency(bill.commission_amt_2)}`
      : '';

  return (
    <tr className={`border-b border-gray-100 hover:bg-gray-50 ${bill.payment_status === 'paid' ? 'bg-red-50' : ''} ${bill.side === 'credit' && bill.payment_status !== 'paid' ? 'bg-red-50/40' : ''}`} data-testid={`bill-row-${bill.id}`}>
      <td className="px-2 py-2 text-center">
        <input type="checkbox" checked={isSelected} onChange={() => onToggleSelect(bill.id)}
          className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
          data-testid={`select-bill-${bill.id}`} onClick={(e) => e.stopPropagation()} />
      </td>
      <td className="px-3 py-2"></td>
      <td className="px-2 py-2 text-center">
        <Badge className={`text-[10px] ${bill.type === 'SE' ? (bill.bill_type === 'challan' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700') : 'bg-red-100 text-red-700'}`}>
          {bill.type}{bill.type === 'SE' && bill.bill_type === 'challan' ? ' (CH)' : bill.type === 'SE' ? ' (INV)' : ''}
        </Badge>
      </td>
      <td className="px-3 py-2 font-mono text-xs font-medium text-blue-700">
        <button className="hover:underline cursor-pointer flex items-center gap-1"
          onClick={(e) => { e.stopPropagation(); onViewBill(bill); }}
          data-testid={`bill-link-${bill.id}`}>
          {bill.number} <ExternalLink className="w-3 h-3 text-gray-400" />
        </button>
      </td>
      <td className="px-3 py-2 text-xs text-gray-500">{bill.invoice_number || '-'}</td>
      <td className="px-2 py-2 text-xs text-gray-600 whitespace-nowrap">{fmtDate(bill.date)}</td>
      <td className={`px-3 py-2 text-right text-xs font-medium ${bill.side === 'credit' ? 'text-red-600' : ''}`}>{bill.side === 'credit' ? '-' : ''}{fmtCurrency(bill.amount)}</td>
      <td className="px-2 py-2 text-right text-xs text-amber-700">
        {bill.discount_amount > 0 ? (
          <span title={`${bill.discount_pct}%`}>{bill.side === 'credit' ? '-' : ''}{fmtCurrency(bill.discount_amount)}</span>
        ) : '-'}
      </td>
      <td className={`px-3 py-2 text-right text-xs font-medium ${bill.side === 'credit' ? 'text-red-600' : ''}`}>
        {bill.side === 'credit' ? '-' : ''}{fmtCurrency(bill.net_amount)}
      </td>
      <td className="px-2 py-2 text-center">
        {bill.is_manual ? (
          <span className="text-xs" title={commTooltip}>Manual</span>
        ) : (
          <div className="flex flex-col items-center leading-tight">
            <span className="text-xs">{bill.commission_pct}%</span>
            {hasDouble && <span className="text-xs text-indigo-500">+ {bill.commission_pct_2}%</span>}
          </div>
        )}
        <button className="ml-1 text-blue-500 hover:text-blue-700" onClick={() => onEditBill(bill, agentId)} data-testid={`edit-bill-${bill.id}`}><Pencil className="w-3 h-3 inline" /></button>
      </td>
      <td className={`px-3 py-2 text-right text-xs font-medium ${bill.side === 'credit' ? 'text-red-600' : 'text-indigo-700'}`} title={commTooltip}>
        {bill.side === 'credit' ? '-' : ''}{fmtCurrency(bill.commission_amount)}
        {hasDouble && bill.commission_amt_2 > 0 && !bill.is_manual && (
          <span className="block text-xs text-gray-400">{fmtCurrency(bill.commission_amt_1)}+{fmtCurrency(bill.commission_amt_2)}</span>
        )}
      </td>
      <td className="px-2 py-2 text-center">{bill.is_billed ? <CircleCheck className="w-4 h-4 text-green-600 inline" /> : <CircleAlert className="w-4 h-4 text-yellow-500 inline" />}</td>
      <td className="px-2 py-2 text-center">
        {bill.payment_status === 'paid' ? (
          isAdmin ? <button className="text-xs font-medium px-2 py-1 rounded-md border cursor-pointer bg-green-100 text-green-700 border-green-300" onClick={() => onPayment(bill, agentId)} data-testid={`payment-btn-${bill.id}`}>Paid</button>
          : <span className="text-xs font-medium px-2 py-1 rounded-md bg-green-100 text-green-700" data-testid={`payment-btn-${bill.id}`}>Paid</span>
        ) : (
          <button className="text-xs font-medium px-2 py-1 rounded-md border cursor-pointer bg-orange-100 text-orange-700 border-orange-300"
            onClick={() => onPayment(bill, agentId)} data-testid={`payment-btn-${bill.id}`}>Unpaid</button>
        )}
      </td>
      <td className="px-2 py-2 text-xs text-gray-600 whitespace-nowrap">{bill.payment_status === 'paid' ? fmtDate(bill.payment_date) : '-'}</td>
    </tr>
  );
};

export default BillRow;
